import type {
  GuidedThread,
  ReadingProgress,
  ThreadProgress,
  ThreadsIndex,
} from '../../data/schema';
import { allThreads } from '../../data/threads';
import type { StudyStore } from '../../services/studyStore';
import {
  createShareButton,
  setShareMetadata,
} from '../../services/shareMetadata';

type ThreadFilter = 'all' | 'theme' | 'verse' | 'speaker';

function routerLink(
  href: string,
  label: string,
  className?: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  if (className) link.className = className;
  return link;
}

function completedTalkIds(
  thread: GuidedThread,
  readingProgress: ReadingProgress[],
  savedProgress: ThreadProgress | undefined,
): Set<string> {
  const completed = new Set(savedProgress?.completedTalkIds ?? []);
  for (const progress of readingProgress) {
    if (progress.completed && thread.steps.some((step) => step.talkId === progress.talkId)) {
      completed.add(progress.talkId);
    }
  }
  return completed;
}

function progressLabel(
  thread: GuidedThread,
  completed: Set<string>,
  savedProgress: ThreadProgress | undefined,
): string {
  if (savedProgress?.completed || completed.size >= thread.steps.length) {
    return 'Completed';
  }
  if (completed.size === 0) return 'Not started';
  return `${String(completed.size)} of ${String(thread.steps.length)} complete`;
}

function createProgressBar(
  thread: GuidedThread,
  completed: Set<string>,
  savedProgress: ThreadProgress | undefined,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'thread-progress';
  const label = document.createElement('span');
  label.textContent = progressLabel(thread, completed, savedProgress);
  const meter = document.createElement('span');
  meter.className = 'thread-progress__track';
  meter.setAttribute('role', 'progressbar');
  meter.setAttribute('aria-valuemin', '0');
  meter.setAttribute('aria-valuemax', String(thread.steps.length));
  const value =
    savedProgress?.completed === true ? thread.steps.length : completed.size;
  meter.setAttribute('aria-valuenow', String(value));
  meter.setAttribute('aria-label', `${thread.title} progress`);
  const fill = document.createElement('i');
  fill.style.width = `${String((value / thread.steps.length) * 100)}%`;
  meter.append(fill);
  wrapper.append(meter, label);
  return wrapper;
}

function createThreadCard(
  thread: GuidedThread,
  readingProgress: ReadingProgress[],
  threadProgress: ThreadProgress[],
): HTMLElement {
  const card = document.createElement('article');
  card.className = `thread-card thread-card--${thread.type}`;
  card.dataset.threadType = thread.type;
  const kicker = document.createElement('p');
  kicker.className = 'eyebrow eyebrow--gold';
  kicker.textContent = thread.subtitle;
  const heading = document.createElement('h3');
  heading.append(
    routerLink(`/threads/${thread.threadId}`, thread.title),
  );
  const meta = document.createElement('p');
  meta.className = 'muted';
  meta.textContent = `${String(thread.steps.length)} ${
    thread.steps.length === 1 ? 'talk' : 'talks'
  } · ${String(thread.estimatedMinutes)} min`;
  const intro = document.createElement('p');
  intro.className = 'thread-card__intro';
  intro.textContent = thread.intro.text;
  const saved = threadProgress.find(
    (progress) => progress.threadId === thread.threadId,
  );
  card.append(
    kicker,
    heading,
    meta,
    intro,
    createProgressBar(
      thread,
      completedTalkIds(thread, readingProgress, saved),
      saved,
    ),
  );
  return card;
}

function createThreadSection(
  title: string,
  description: string,
  threads: GuidedThread[],
  readingProgress: ReadingProgress[],
  threadProgress: ThreadProgress[],
): HTMLElement {
  const section = document.createElement('section');
  section.className = 'thread-section';
  section.dataset.threadSection = threads[0]?.type ?? '';
  const heading = document.createElement('div');
  heading.className = 'section-heading';
  const titleElement = document.createElement('h2');
  titleElement.textContent = title;
  const count = document.createElement('span');
  count.textContent = `${String(threads.length)} paths`;
  heading.append(titleElement, count);
  const copy = document.createElement('p');
  copy.className = 'thread-section__copy';
  copy.textContent = description;
  const cards = document.createElement('div');
  cards.className = 'thread-card-grid';
  for (const thread of threads) {
    cards.append(createThreadCard(thread, readingProgress, threadProgress));
  }
  section.append(heading, copy, cards);
  return section;
}

export function createThreadsIndex(
  index: ThreadsIndex,
  readingProgress: ReadingProgress[],
  threadProgress: ThreadProgress[],
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'threads-index page-width';
  page.dataset.route = 'threads-index';
  const heading = document.createElement('header');
  heading.className = 'threads-heading';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Guided study';
  const title = document.createElement('h1');
  title.textContent = 'Reading Threads';
  const copy = document.createElement('p');
  copy.textContent =
    'Follow guided paths through shared themes, authoritative scriptures, and speakers.';
  heading.append(eyebrow, title, copy);

  const filters = document.createElement('div');
  filters.className = 'thread-filters';
  filters.setAttribute('role', 'group');
  filters.setAttribute('aria-label', 'Filter reading threads');
  const filterOptions: Array<[ThreadFilter, string]> = [
    ['all', 'All'],
    ['theme', 'Themes'],
    ['verse', 'Scriptures'],
    ['speaker', 'Speakers'],
  ];
  const sections: HTMLElement[] = [];
  const applyFilter = (filter: ThreadFilter): void => {
    for (const button of filters.querySelectorAll<HTMLButtonElement>('button')) {
      const selected = button.dataset.filter === filter;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-active', selected);
    }
    for (const section of sections) {
      section.hidden =
        filter !== 'all' && section.dataset.threadSection !== filter;
    }
  };
  for (const [filter, label] of filterOptions) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'thread-filter';
    button.dataset.filter = filter;
    button.textContent = label;
    button.addEventListener('click', () => applyFilter(filter));
    filters.append(button);
  }

  sections.push(
    createThreadSection(
      'Theme Threads',
      'Ordered by theme prominence across the conference.',
      index.themeThreads,
      readingProgress,
      threadProgress,
    ),
    createThreadSection(
      'Scripture Threads',
      'The ten most relevant authoritative scripture paths in this conference.',
      index.verseThreads.slice(0, 10),
      readingProgress,
      threadProgress,
    ),
    createThreadSection(
      'Speaker Threads',
      'Conference messages grouped by speaker and ordered by session.',
      index.speakerThreads,
      readingProgress,
      threadProgress,
    ),
  );
  applyFilter('all');
  page.append(heading, filters, ...sections);
  return page;
}

function createStep(
  thread: GuidedThread,
  step: GuidedThread['steps'][number],
  index: number,
  completed: boolean,
  current: boolean,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = `thread-step ${
    completed ? 'thread-step--done' : current ? 'thread-step--current' : ''
  }`;
  wrapper.dataset.talkId = step.talkId;
  const marker = document.createElement('span');
  marker.className = 'thread-step__marker';
  marker.textContent = completed ? '✓' : String(index + 1);
  marker.setAttribute('aria-hidden', 'true');
  const card = document.createElement('article');
  card.className = 'thread-step__card';
  const speaker = document.createElement('p');
  speaker.className = 'thread-step__speaker';
  speaker.textContent = step.speaker;
  const heading = document.createElement('h2');
  heading.textContent = step.title;
  const excerpt = document.createElement('div');
  excerpt.className = 'thread-excerpt';
  for (const paragraph of step.excerpt) {
    const quote = document.createElement('blockquote');
    quote.textContent = paragraph.text;
    const paragraphLink = routerLink(
      `/read/${step.talkId}/${paragraph.paragraphId}?thread=${thread.threadId}`,
      'Read this paragraph in context',
      'text-link',
    );
    quote.append(document.createElement('br'), paragraphLink);
    excerpt.append(quote);
  }
  const footer = document.createElement('div');
  footer.className = 'thread-step__footer';
  const status = document.createElement('span');
  status.textContent = completed
    ? 'Completed'
    : current
      ? 'Up next'
      : 'Not started';
  const time = document.createElement('span');
  time.textContent = `${String(step.readingMinutes)} min`;
  const action = routerLink(
    `/read/${step.talkId}/${step.excerpt[0].paragraphId}?thread=${thread.threadId}`,
    completed ? 'Reread full talk' : 'Read full talk',
    'button button--compact',
  );
  footer.append(status, time, action);
  card.append(speaker, heading, excerpt, footer);
  wrapper.append(marker, card);
  return wrapper;
}

function createCompletionPanel(
  thread: GuidedThread,
  all: GuidedThread[],
  completed: Set<string>,
  savedProgress: ThreadProgress | undefined,
  conferenceId: string,
  studyStore: StudyStore,
): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'thread-completion';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Continue your study';
  const heading = document.createElement('h2');
  heading.textContent =
    savedProgress?.completed || completed.size >= thread.steps.length
      ? 'Thread complete'
      : 'Finish this guided path';
  const copy = document.createElement('p');
  copy.textContent =
    savedProgress?.completed || completed.size >= thread.steps.length
      ? `You have explored ${thread.title} across ${String(thread.steps.length)} ${
          thread.steps.length === 1 ? 'talk' : 'talks'
        }.`
      : 'Complete each talk in the Reader, or mark the thread complete when your study is finished.';
  const status = document.createElement('p');
  status.className = 'inline-status';
  status.setAttribute('role', 'status');
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'button';
  button.textContent =
    savedProgress?.completed || completed.size >= thread.steps.length
      ? 'Completed'
      : 'Mark thread complete';
  button.disabled =
    savedProgress?.completed || completed.size >= thread.steps.length;
  button.addEventListener('click', () => {
    button.disabled = true;
    void studyStore
      .saveThreadProgress(
        conferenceId,
        thread.threadId,
        thread.steps.map((step) => step.talkId),
        true,
      )
      .then(() => {
        button.textContent = 'Completed';
        heading.textContent = 'Thread complete';
        status.textContent = 'Thread completion saved.';
      })
      .catch(() => {
        button.disabled = false;
        status.textContent =
          'Sign in or reconnect to save thread completion.';
      });
  });
  const relatedHeading = document.createElement('h3');
  relatedHeading.textContent = 'Related threads';
  const related = document.createElement('div');
  related.className = 'thread-related';
  for (const threadId of thread.relatedThreadIds) {
    const item = all.find((candidate) => candidate.threadId === threadId);
    if (!item) continue;
    related.append(
      routerLink(`/threads/${item.threadId}`, item.title, 'thread-related__link'),
    );
  }
  panel.append(eyebrow, heading, copy, button, status, relatedHeading, related);
  return panel;
}

export function createThreadDetail(
  thread: GuidedThread,
  index: ThreadsIndex,
  readingProgress: ReadingProgress[],
  threadProgress: ThreadProgress[],
  conferenceId: string,
  studyStore: StudyStore,
): HTMLElement {
  const page = document.createElement('div');
  page.className = `thread-detail thread-detail--${thread.type} page-width`;
  page.dataset.route = 'thread';
  page.dataset.threadId = thread.threadId;
  const saved = threadProgress.find(
    (progress) => progress.threadId === thread.threadId,
  );
  const completed = completedTalkIds(thread, readingProgress, saved);
  const header = document.createElement('header');
  header.className = 'thread-detail__header';
  const kicker = document.createElement('p');
  kicker.className = 'eyebrow';
  kicker.textContent = thread.subtitle;
  const heading = document.createElement('h1');
  heading.textContent = thread.title;
  const meta = document.createElement('p');
  meta.textContent = `${String(thread.steps.length)} ${
    thread.steps.length === 1 ? 'talk' : 'talks'
  } · ${String(thread.estimatedMinutes)} min · ${thread.orderRule.replaceAll('-', ' ')}`;
  header.append(
    kicker,
    heading,
    meta,
    createProgressBar(thread, completed, saved),
    createShareButton(
      'Copy thread link',
      `/threads/${thread.threadId}`,
      thread.title,
    ),
  );
  setShareMetadata({
    title: thread.title,
    description: thread.intro.text,
    pathname: `/threads/${thread.threadId}`,
  });

  const body = document.createElement('div');
  body.className = 'thread-detail__body';
  const intro = document.createElement('section');
  intro.className = 'thread-intro';
  const introCopy = document.createElement('p');
  introCopy.textContent = thread.intro.text;
  intro.append(introCopy);
  if (thread.type === 'verse') {
    const links = document.createElement('div');
    links.className = 'thread-source-links';
    const sci = document.createElement('a');
    sci.href = thread.scripture.sciUrl;
    sci.target = '_blank';
    sci.rel = 'noreferrer';
    sci.textContent = 'Open in Scripture Citation Index';
    const official = document.createElement('a');
    official.href = thread.scripture.officialChurchUrl;
    official.target = '_blank';
    official.rel = 'noreferrer';
    official.textContent = 'Read in Gospel Library';
    links.append(sci, official);
    intro.append(links);
  }
  body.append(intro);

  const firstIncomplete = thread.steps.findIndex(
    (step) => !completed.has(step.talkId),
  );
  for (const [position, step] of thread.steps.entries()) {
    body.append(
      createStep(
        thread,
        step,
        position,
        completed.has(step.talkId),
        position === (firstIncomplete < 0 ? 0 : firstIncomplete),
      ),
    );
    const connection = thread.connections.find(
      (item) =>
        item.fromTalkId === step.talkId &&
        item.toTalkId === thread.steps[position + 1]?.talkId,
    );
    if (connection) {
      const note = document.createElement('p');
      note.className = 'thread-connection';
      note.textContent = connection.note;
      body.append(note);
    }
  }
  body.append(
    createCompletionPanel(
      thread,
      allThreads(index),
      completed,
      saved,
      conferenceId,
      studyStore,
    ),
  );
  page.append(header, body);
  return page;
}
